import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatWorkflowStateLabel, type AppToastDetail } from '@/lib/utils';

type ToastTone = 'info' | 'success' | 'warning' | 'error';

interface TransitionToast {
  id: string;
  title: string;
  message: string;
  emittedAt: string;
  tone: ToastTone;
  href?: string | null;
}

interface SseTransitionEvent {
  stagingRowId: string;
  fromState: string;
  toState: string;
  reference?: string | null;
  emittedAt: string;
  happyPath: boolean;
  tone?: ToastTone;
}

interface SseStepActivityEvent {
  id: string;
  stagingRowId: string;
  stage: string;
  substepId: string | null;
  phase: 'started' | 'finished';
  outcome: 'success' | 'warning' | 'error' | null;
  latencyMs: number | null;
  emittedAt: string;
}

declare global {
  interface WindowEventMap {
    'cockpit:transition': CustomEvent<SseTransitionEvent>;
    'cockpit:step-activity': CustomEvent<SseStepActivityEvent>;
  }
}

interface AdminJobEvent {
  job: string;
  phase: 'started' | 'progress' | 'completed' | 'failed';
  message: string;
  level: 'success' | 'error' | 'info';
  emittedAt: string;
}

const TOAST_TTL_MS = 9000;

function resolveTransitionTone(event: SseTransitionEvent): ToastTone {
  if (event.tone) return event.tone;
  return event.happyPath ? 'success' : 'error';
}

function adminLevelTone(level: AdminJobEvent['level']): ToastTone {
  if (level === 'error') return 'error';
  if (level === 'info') return 'info';
  return 'success';
}

export function TransitionToasts(): JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  const seenIds = useRef(new Set<string>());

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let reconnectTimer: number | null = null;
    const pending: TransitionToast[] = [];
    const deduplicationTimers = new Set<number>();
    let active: TransitionToast | null = null;

    function showNextToast(): void {
      if (disposed || active || pending.length === 0) return;
      const next = pending.shift()!;
      active = next;
      // Queued notifications start their lifetime only when they become visible.
      showToast(
        next,
        (href) => navigateRef.current(href),
        () => {
          if (disposed || active?.id !== next.id) return;
          active = null;
          const timer = window.setTimeout(() => {
            seenIds.current.delete(next.id);
            deduplicationTimers.delete(timer);
          }, TOAST_TTL_MS);
          deduplicationTimers.add(timer);
          showNextToast();
        },
      );
    }

    function queueToast(toast: TransitionToast, pageFeedback = false): void {
      if (seenIds.current.has(toast.id)) return;
      seenIds.current.add(toast.id);
      const transitionIndex = pageFeedback ? pending.findIndex((item) => item.href) : -1;
      if (transitionIndex >= 0) pending.splice(transitionIndex, 0, toast);
      else pending.push(toast);
      showNextToast();
    }

    function pushToast(event: SseTransitionEvent): void {
      window.dispatchEvent(
        new CustomEvent<SseTransitionEvent>('cockpit:transition', { detail: event }),
      );
      void queryClient.invalidateQueries({ queryKey: ['cockpit', 'queue'] });
      void queryClient.invalidateQueries({ queryKey: ['cockpit', 'exceptions'] });
      void queryClient.invalidateQueries({ queryKey: ['cockpit', 'drafts'] });
      const id = `${event.stagingRowId}:${event.fromState}:${event.toState}:${event.emittedAt}`;
      queueToast({
        id,
        title: event.reference?.trim() || event.stagingRowId,
        message: `${formatWorkflowStateLabel(event.fromState)} to ${formatWorkflowStateLabel(event.toState)}`,
        emittedAt: event.emittedAt,
        tone: resolveTransitionTone(event),
        href: `/queue/${event.stagingRowId}`,
      });
    }

    function pushStepActivity(event: SseStepActivityEvent): void {
      window.dispatchEvent(
        new CustomEvent<SseStepActivityEvent>('cockpit:step-activity', { detail: event }),
      );
    }

    function pushAdminToast(admin: AdminJobEvent): void {
      const id = `${admin.job}:${admin.phase}:${admin.emittedAt}`;
      queueToast({
        id,
        title: admin.job,
        message: admin.message,
        emittedAt: admin.emittedAt,
        tone: adminLevelTone(admin.level),
        href: null,
      });
      // Curator events flip feedback_records.state -> superseded /
      // captured. Invalidate the open Feedback page so cards animate
      // between lanes in real time instead of waiting for a page refresh.
      if (admin.job === 'feedback_curator' && admin.phase === 'completed') {
        void queryClient.invalidateQueries({ queryKey: ['cockpit', 'feedback'] });
      }
    }

    function pushAppToast(appToast: AppToastDetail): void {
      const emittedAt = new Date().toISOString();
      queueToast(
        {
          id: `app:${appToast.title}:${appToast.message ?? ''}:${emittedAt}`,
          title: appToast.title,
          message: appToast.message ?? '',
          emittedAt,
          tone: appToast.tone ?? 'success',
          href: null,
        },
        true,
      );
    }

    async function connectOnce(): Promise<void> {
      const response = await fetch('/api/v1/cockpit/events', {
        headers: { Accept: 'text/event-stream' },
        credentials: 'include',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`sse_connect_failed:${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!disposed) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const rawEvent = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const eventType =
            rawEvent
              .split('\n')
              .find((line) => line.startsWith('event:'))
              ?.slice(6)
              .trim() ?? 'message';
          const dataLines = rawEvent
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trim());
          if (dataLines.length > 0) {
            try {
              const parsed = JSON.parse(dataLines.join('\n')) as
                | SseTransitionEvent
                | AdminJobEvent
                | SseStepActivityEvent;
              if (eventType === 'transition') {
                pushToast(parsed as SseTransitionEvent);
              } else if (eventType === 'admin_job') {
                pushAdminToast(parsed as AdminJobEvent);
              } else if (eventType === 'step_activity') {
                pushStepActivity(parsed as SseStepActivityEvent);
              }
            } catch {
              // ignore malformed events
            }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
    }

    async function maintainConnection(): Promise<void> {
      while (!disposed) {
        try {
          await connectOnce();
        } catch {
          if (disposed || controller.signal.aborted) break;
        }
        if (disposed) break;
        await new Promise<void>((resolve) => {
          reconnectTimer = window.setTimeout(() => resolve(), 2000);
        });
      }
    }

    void maintainConnection();

    function onAppToast(event: Event): void {
      const detail = (event as CustomEvent<AppToastDetail>).detail;
      if (!detail) return;
      pushAppToast(detail);
    }

    window.addEventListener('app:toast', onAppToast as EventListener);

    return () => {
      disposed = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      controller.abort();
      pending.length = 0;
      deduplicationTimers.forEach((timer) => window.clearTimeout(timer));
      seenIds.current.clear();
      if (active) toast.dismiss(active.id);
      window.removeEventListener('app:toast', onAppToast as EventListener);
    };
  }, [queryClient]);

  return null;
}

function showToast(
  toastEvent: TransitionToast,
  navigate: (to: string) => void,
  onComplete: () => void,
): void {
  const options = {
    id: toastEvent.id,
    description: toastEvent.message,
    duration: TOAST_TTL_MS,
    onDismiss: onComplete,
    onAutoClose: onComplete,
    action: toastEvent.href
      ? {
          label: 'Open',
          onClick: () => {
            navigate(toastEvent.href ?? '/queue');
            onComplete();
          },
        }
      : undefined,
  };
  switch (toastEvent.tone) {
    case 'error':
      toast.error(toastEvent.title, options);
      break;
    case 'warning':
      toast.warning(toastEvent.title, options);
      break;
    case 'info':
      toast.info(toastEvent.title, options);
      break;
    case 'success':
      toast.success(toastEvent.title, options);
      break;
  }
}
